import React, { useEffect, useRef } from "react";
import { FluentResource } from "@fluent/bundle";
import { LocalizationProvider, Localized } from "@fluent/react";

import useUpdate from "@kikoff/hooks/src/useUpdate";
import { combineClasses, passwordScore } from "@kikoff/utils/src/string";

import { newReactLocalization } from "@util/l10n";

import styles from "./password.module.scss";

const RESOURCES = {
  en: new FluentResource(`password-requirements = Password Requirements
password-must-not-be-weak = Must be at least 8 characters long
password-must-be-long = Must not be very weak
password-score-very-weak = Very Weak
password-score-weak = Weak
password-score-fair = Fair
password-score-strong = Strong
password-score-very-strong = Very Strong
`),
  es: new FluentResource(`password-requirements = Requisitos de Contraseña
password-must-not-be-weak = Debe tener al menos 8 caracteres de longitud
password-must-be-long = No debe ser muy débil
password-score-very-weak = Muy Débil
password-score-weak = Débil
password-score-fair = Justo
password-score-strong = Fuerte
password-score-very-strong = Muy Fuerte
`),
};

export type PasswordStrengthProps = {
  showLabel?: boolean;
  className?: string;
} & (
  | {
      password: string;
      onScoreChange?(score: number): void;
    }
  | {
      score: number | Promise<number>;
    }
);

export const PasswordStrength: React.FC<PasswordStrengthProps> = ({
  showLabel,
  className,
  ...props
}) => {
  // This is a ref to avoid rerender if the score is the same after
  // passwordScore is evaluated, if this was a state hook we wouldn't be able to
  // compare to the most recent value
  const score = useRef<number>(null);
  const update = useUpdate();

  if ("password" in props)
    useEffect(() => {
      passwordScore(props.password).then((_score) => {
        if (score.current === _score) return;
        score.current = _score;
        props.onScoreChange?.(_score);
        update();
      });
    }, [props.password]);
  else
    useEffect(() => {
      (async () => {
        score.current = await props.score;
        update();
      })();
    }, [props.score]);

  const empty =
    "password" in props ? !props.password : typeof props.score !== "number";

  const l10n = newReactLocalization(RESOURCES);

  const humanizedPasswordScore = l10n.getString(
    [
      "password-score-very-weak",
      "password-score-weak",
      "password-score-fair",
      "password-score-strong",
      "password-score-very-strong",
    ][score.current]
  );

  return (
    <div
      className={combineClasses(styles["password-strength"], className)}
      style={{ height: !empty && showLabel ? 15 : 5 }}
    >
      <div
        className={styles.bar}
        style={
          !empty
            ? [
                { background: "red", width: "20%" },
                { background: "orange", width: "40%" },
                { background: "yellow", width: "60%" },
                { background: "#5e5", width: "80%" },
                { background: "#7cf", width: "100%" },
              ][score.current]
            : { width: "0" }
        }
      />
      <div
        className={styles.score}
        style={{
          opacity: !empty && showLabel ? 1 : 0,
        }}
      >
        {humanizedPasswordScore}
      </div>
    </div>
  );
};

interface PasswordRequirementsProps {
  className?: string;
  reqs: { description: React.ReactNode; valid: boolean }[];
}

export function PasswordRequirements({
  reqs,
  className,
}: PasswordRequirementsProps) {
  const l10n = newReactLocalization(RESOURCES);

  return (
    <div className={combineClasses(styles["password-requirements"], className)}>
      <LocalizationProvider l10n={l10n}>
        <header>
          <Localized id="password-requirements">
            Password requirements
          </Localized>
          :
        </header>
        {reqs.map(({ description, valid }, i) => (
          <div
            className={styles.requirement}
            key={i}
            style={{ ...(!valid && { color: "#aaa" }) }}
          >
            <span style={{ color: valid ? "#0c0" : "red" }}>
              {valid ? "" : ""}
            </span>
            {description}
          </div>
        ))}
      </LocalizationProvider>
    </div>
  );
}
